"use client";

// @/app/components/WhatsAppButton.tsx

import { useCallback, type MouseEvent } from "react";
import { SiWhatsapp } from "@icons-pack/react-simple-icons";

import { sendMetaTrackPayload } from "@/lib/meta-track-client";
import {
  appendLeadAttribution,
  createCtaEventId,
  getOrCreateLeadRef,
  getSessionInboundSourceCode,
  normalizeCtaPlacement,
} from "@/lib/cta-analytics";
import { trackJourneyEvent } from "@/lib/journey-analytics";
import {
  DEFAULT_WHATSAPP_ENQUIRY_MESSAGE,
  ONE_TO_ONE_WHATSAPP_PHONE,
} from "@/lib/whatsapp";

interface Props {
  message?: string;
  /** Direct link override (for example, WhatsApp group invite links). */
  href?: string;
  /**
   * Block-level surface for GA source_surface: where on the page this
   * button sits (e.g. "hero", "closing_cta"). Do NOT encode page identity
   * here — GA4 page_location / first-party page_path already carry that.
   */
  placement?: string;
  className?: string;
  /** Button label (defaults to "Consult Us Now") */
  label?: string;
  /** Icon-only on mobile, full label on md+ */
  compact?: boolean;
  /**
   * Stable machine id of the click destination for GA target_id; override
   * for group-invite variants (e.g. "whatsapp_lab_group").
   */
  targetId?: string;
  /** Separates student enquiries from careers applications in analytics. */
  purpose?: "student_lead" | "recruitment";
}

const defaultPlacement = "unknown";
const defaultClasses = `
    inline-flex min-h-10 items-center whitespace-nowrap
    no-underline hover:no-underline focus:no-underline
    bg-whatsapp text-[#0d1f4c]
    text-shadow-sm font-semibold
    px-2 py-1.5 text-sm
    md:px-5 md:py-2.5
    lg:min-h-12 lg:py-3.5 lg:text-lg xl:px-8
	    rounded-full shadow-md
	    transition duration-300
	    hover:bg-whatsapp-dark hover:text-white hover:shadow-lg
	    active:bg-whatsapp-darker active:text-white active:shadow-lg
	    focus:outline-none focus-visible:ring-4 focus-visible:ring-whatsapp-dark
	  `;

export default function WhatsAppButton({
  message = DEFAULT_WHATSAPP_ENQUIRY_MESSAGE,
  href,
  placement = defaultPlacement,
  className = "",
  label = "Consult Us Now",
  compact = false,
  targetId = "whatsapp_consult",
  purpose = "student_lead",
}: Props) {
  const normalizedPlacement = normalizeCtaPlacement(placement);
  const isRecruitment = purpose === "recruitment";
  const resolvedTargetId = isRecruitment ? "whatsapp_careers" : targetId;
  const journeyName = isRecruitment
    ? "careers_whatsapp_application"
    : "shared_whatsapp_cta";
  const ctaFamily = isRecruitment ? "recruitment_cta" : "lead_cta";
  const commercialIntent = isRecruitment ? "recruitment" : "direct";
  const isGroupInvite =
    resolvedTargetId === "whatsapp_lab_group" ||
    Boolean(href?.startsWith("https://chat.whatsapp.com/"));
  const resolvedHref =
    href ??
    `https://wa.me/${ONE_TO_ONE_WHATSAPP_PHONE}?text=${encodeURIComponent(
      message
    )}`;
  const classes = `${defaultClasses} ${className}`.replace(/\s+/g, " ").trim();

  const handleClick = useCallback(
    (clickEvent: MouseEvent<HTMLAnchorElement>) => {
      if (typeof window === "undefined") return;

      const eventId = createCtaEventId();
      const leadRef = isGroupInvite ? undefined : getOrCreateLeadRef();
      const inboundSourceCode = isGroupInvite
        ? null
        : getSessionInboundSourceCode();

      if (!isGroupInvite && leadRef) {
        clickEvent.currentTarget.href = `https://wa.me/${ONE_TO_ONE_WHATSAPP_PHONE}?text=${encodeURIComponent(
          appendLeadAttribution(message, leadRef, inboundSourceCode)
        )}`;
      }

      trackJourneyEvent("whatsapp_click", {
        commercial_intent: commercialIntent,
        cta_family: ctaFamily,
        event_id: eventId,
        journey_name: journeyName,
        ...(inboundSourceCode
          ? { inbound_source_code: inboundSourceCode }
          : {}),
        ...(leadRef ? { lead_ref: leadRef } : {}),
        source_surface: normalizedPlacement,
        target_id: resolvedTargetId,
        target_stage: "lead_intent",
        target_type: isGroupInvite ? "group_channel" : "channel",
        is_primary_cta: true,
      });

      const getCookie = (name: string) => {
        const pair = document.cookie
          .split("; ")
          .find((row) => row.startsWith(`${name}=`));
        if (!pair) return undefined;
        const [, value] = pair.split("=");
        return value ? decodeURIComponent(value) : undefined;
      };

      const fbp = getCookie("_fbp");
      const fbc = getCookie("_fbc");

      // Pixel event
      if (typeof window.fbq === "function") {
        window.fbq("track", "Contact", {}, { eventID: eventId });
      }

      // CAPI event via our API route
      const payload = {
        event_name: "Contact",
        event_id: eventId,
        event_source_url: window.location.href,
        fbp,
        fbc,
      };
      const body = JSON.stringify(payload);

      sendMetaTrackPayload(body);
    },
    [
      commercialIntent,
      ctaFamily,
      isGroupInvite,
      journeyName,
      message,
      normalizedPlacement,
      resolvedTargetId,
    ]
  );

  return (
    <a
      href={resolvedHref}
      target="_blank"
      rel="noopener noreferrer"
      className={classes}
      suppressHydrationWarning
      data-nosnippet
      data-ga-event="whatsapp_click"
      data-ga-manual="true"
      data-ga-impression="true"
      data-ga-category="CTA"
      data-ga-journey={journeyName}
      data-ga-source-surface={normalizedPlacement}
      data-ga-target-stage="lead_intent"
      data-ga-target-type={isGroupInvite ? "group_channel" : "channel"}
      data-ga-target-id={resolvedTargetId}
      data-ga-cta-family={ctaFamily}
      data-ga-commercial-intent={commercialIntent}
      data-ga-primary="true"
      onClick={handleClick}
    >
      {compact ? (
        <>
          <SiWhatsapp size={20} className="xl:mr-2" aria-hidden="true" />
          <span className="hidden xl:inline">{label}</span>
          <span className="sr-only xl:hidden">{label}</span>
        </>
      ) : (
        label
      )}
    </a>
  );
}
